import { useState } from "react";
import { toast } from "sonner";
import { Star, ExternalLink, X } from "lucide-react";
import { api, apiError } from "../lib/api";

const Stars = ({ value, hover, onHover, onPick }) => (
  <div className="flex items-center gap-1" onMouseLeave={() => onHover(0)}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`review-star-${n}`}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        onMouseEnter={() => onHover(n)}
        onClick={() => onPick(n)}
        className="p-0.5 transition-transform hover:scale-110"
      >
        <Star
          className={`w-7 h-7 ${
            n <= (hover || value) ? "fill-amber-400 text-amber-400" : "text-slate-300"
          }`}
          strokeWidth={1.5}
        />
      </button>
    ))}
  </div>
);

// Reusable in-app review widget (star rating + comment, one per user, updatable).
// 4-5 star ratings get a Google review invitation IF the backend has GOOGLE_REVIEWS_URL set.
export const ReviewWidget = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [existing, setExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const openForm = async () => {
    setOpen(true);
    setResult(null);
    try {
      const { data } = await api.get("/reviews/mine");
      if (data.review) {
        setRating(data.review.rating);
        setComment(data.review.comment || "");
        setExisting(true);
      }
    } catch { /* prefill is best-effort */ }
  };

  const close = () => { setOpen(false); setHover(0); };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { rating, comment: comment.trim() });
      setResult(data);
      setExisting(true);
    } catch (e) {
      toast.error(apiError(e, "Could not save your review"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid="review-open-btn"
        onClick={openForm}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 underline transition-colors"
      >
        <Star className="w-3.5 h-3.5" /> Leave a review
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={close} />
      <div data-testid="review-modal" className="relative bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] max-w-md w-full p-6">
        <button data-testid="review-close-btn" onClick={close}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors">
          <X className="w-4 h-4" />
        </button>

        {result ? (
          <div data-testid="review-thanks" className="text-center py-4">
            <Star className="w-10 h-10 fill-amber-400 text-amber-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900">Thank you for your review!</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Your feedback helps us make the practice exams better for every student.
            </p>
            {result.google_review_url && (
              <a
                data-testid="google-review-link"
                href={result.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors"
              >
                Share it on Trustpilot too <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <div className="mt-4">
              <button data-testid="review-done-btn" onClick={close}
                className="text-sm text-slate-500 hover:text-slate-700 underline transition-colors">
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-slate-900">
              {existing ? "Update your review" : "How are we doing?"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">Rate the practice exams and tell us what you think.</p>
            <div className="mt-4">
              <Stars value={rating} hover={hover} onHover={setHover} onPick={setRating} />
            </div>
            <textarea
              data-testid="review-comment-input"
              value={comment}
              maxLength={500}
              rows={3}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional: what's working well, what could be better? (max 500 characters)"
              className="mt-4 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button data-testid="review-cancel-btn" onClick={close}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="review-submit-btn"
                disabled={busy || rating === 0}
                onClick={submit}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white transition-colors"
              >
                {busy ? "Saving…" : existing ? "Update review" : "Submit review"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
